import { Directive, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AuthService } from 'src/app/services/auth.service';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { ConfigService } from 'src/app/services/config.service';
import {
    DashboardService,
    IDashboardInitOptions,
} from 'src/app/services/dashboard/dashboard.service';
import { DataEnvironmentService } from 'src/app/services/data-environment/data-environment.service';
import { IDataLoader } from 'src/app/services/data-loader/IDataLoader';
import { DataService } from 'src/app/services/data.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { NotificationService } from 'src/app/services/notification.service';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { DashboardView } from 'src/app/services/query-context/models/dashboard-view.model';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    OperationCancelledError,
    PromiseCancellationToken,
} from 'src/app/utils/promise/cancellation-token';

/**
 * Represents a base class for standard dashboard functionality.
 * This abstract class provides a framework for managing dashboard data loaders,
 * handling query contexts, and interacting with related services.
 */
@Directive()
export abstract class StandardDashboardWorker implements OnInit {
    abstract filterDataLoaders: Record<string, IDataLoader>;
    abstract chartDataLoaders: Record<string, IDataLoader>;
    /** Friendly name of the dashboard */
    abstract DASHBOARD_NAME: string;
    /** Unique identifier of the dashboard */
    abstract DASHBOARD_ID: string;
    /** Initial dashboard options */
    dashboardInitOptions?: IDashboardInitOptions;

    // The name of the class, used for logging and debugging purposes.
    classname: string;

    // Token for managing cancellable promises in the dashboard context.
    cancellationToken: PromiseCancellationToken =
        new PromiseCancellationToken();
    filterCancellationToken: PromiseCancellationToken =
        new PromiseCancellationToken();

    // Indicates whether the apply button is enabled or disabled.
    public isApplyButtonDisabled = true;

    // The initial query context for the dashboard.
    public initialContext: IQueryContext = QueryContextService.INITIAL_CONTEXT;

    // Default dashboard documentation.
    public dashboardDocumentation = 'Default dashboard documentation';

    constructor(
        private queryContextService: QueryContextService,
        private legendService: CommonLegendService,
        public queryCache: QueryCacheService,
        public notification: NotificationService,
        public activatedRoute: ActivatedRoute,
        public router: Router,
        public data: DataService,
        public colorService: ChartColorService,
        public authService: AuthService,
        public analyticsService: AnalyticsService,
        public loggerService: LoggerService,
        public dashboardService: DashboardService,
        private modal: NzModalService,
        private config: ConfigService,
        public dataEnv: DataEnvironmentService
    ) {
        this.classname = this.constructor.name;
    }

    ngOnInit(): void {
        let options = this.dashboardInitOptions;
        this.dashboardService.initializeDashboard(
            this.DASHBOARD_NAME,
            this.DASHBOARD_ID,
            this.getInitialContext(),
            this.dashboardInitOptions
        );

        // cannot get ActivetedRoute to work, so using simple JS way
        var paramEnv = new URLSearchParams(window.location.search).get('env');
        this.dataEnv.initFromParam(paramEnv);
    }

    /**
     * Initializes the standard dashboard. Sets up context listeners and initializes contexts.
     *
     * @returns A promise that resolves when initialization is complete.
     */
    async standardDashboardInitialize(
        initOptions?: IDashboardInitOptions
    ): Promise<void> {
        this.loggerService.info(
            'StandardDashboardWorker.standardDashboardInitialize'
        );
        this.dashboardInitOptions = initOptions;
        this.queryContextService.pendingQueryContextEmitter.subscribe(
            async (context) => {
                this.isApplyButtonDisabled = false;
                await this.changePendingQueryContextHandler(context.context);
            }
        );
        this.queryContextService.appliedQueryContextEmitter.subscribe(
            async (context) => {
                this.isApplyButtonDisabled = true;
                await this.changeAppliedQueryContextHandler(context.context);
            }
        );

        const contextId =
            this.activatedRoute.snapshot.queryParamMap.get('contextId');
        // initialize contexts with saved context or provided context select logic or initial context
        const dashboardView = await this.getSavedDashboardViewFromURL();
        if (dashboardView) {
            this.dashboardService.loadSavedDashboardView(dashboardView.id, {
                reroute: false,
            });
        } else if (this.dashboardInitOptions?.selectInitialDashboardViewClb) {
            let views = await this.dashboardService.getNamedDashboardViews();

            const selectedDashboardView =
                this.dashboardInitOptions?.selectInitialDashboardViewClb(views);

            this.dashboardService.loadSavedDashboardView(
                selectedDashboardView.id,
                {
                    // reroute to new url
                    reroute: true,
                }
            );
        } else {
            this.queryContextService.initializeContext(
                this.getInitialContext()
            );

            // dashboard context
            this.dashboardService.setDashboardContext({}, true);
        }
    }

    /** Restores the initial context of the dashboard. */
    async restoreInitialContext() {
        this.dashboardService.restoreInitialContext();
    }

    /**
     * Abstract method to get the initial query context.
     * Must be implemented by subclasses to define the initial context of the dashboard.
     *
     * @returns The initial query context.
     */
    abstract getInitialContext(): IQueryContext;

    /**
     * Handles changes to the applied query context.
     * Cancels ongoing operations and triggers new data loading based on the applied context.
     *
     * @param context - The new query context that has been applied.
     */
    async changeAppliedQueryContextHandler(context: IQueryContext) {
        this.loggerService.debug(
            `StandardDashboardWorker.changeAppliedQueryContextHandler`,
            {
                context,
            }
        );
        this.analyticsService.events.contextApplied(context);
        // cancel and recreate token
        this.cancellationToken.cancel();
        this.filterCancellationToken.cancel();
        // create new instance in another place in memory, currently running functions will still use previous reference
        this.cancellationToken = new PromiseCancellationToken();
        this.filterCancellationToken = new PromiseCancellationToken();

        await this.changeAppliedQueryContextImpl(
            context,
            this.cancellationToken,
            this.filterCancellationToken
        ).catch((e) => {
            if (e instanceof OperationCancelledError) {
                this.loggerService.error(e.message);
                return;
            }
            throw e;
        });
    }

    /**
     * Implementation method for changing the applied query context.
     * Responsible for running data loaders and updating the dashboard based on the new context.
     *
     * @param context - The new query context.
     * @param chartCancellationToken - Token for cancelling chart loading operations.
     * @param filterCancellationToken - Token for cancelling filter loading operations.
     */
    async changeAppliedQueryContextImpl(
        context: IQueryContext,
        chartCancellationToken: PromiseCancellationToken,
        filterCancellationToken: PromiseCancellationToken
    ) {
        chartCancellationToken.throwIfCancellationRequested();
        filterCancellationToken.throwIfCancellationRequested();

        // execute each loader
        const chartDataLoadersPromises = Object.values(
            this.chartDataLoaders
        ).map(async (loader) => {
            await loader.run(structuredClone(context), chartCancellationToken);
        });

        // execute each loader
        const filterDataLoadersPromises = Object.values(
            this.filterDataLoaders
        ).map(async (loader) => {
            await loader.run(structuredClone(context), filterCancellationToken);
        });

        this.legendService.showLoading();
        await Promise.all([
            ...chartDataLoadersPromises,
            ...filterDataLoadersPromises,
        ]);
        this.legendService.hideLoading();
    }

    /**
     * Handles changes to the pending query context.
     * Cancels ongoing operations and prepares for new context application.
     *
     * @param context - The new pending query context.
     */
    async changePendingQueryContextHandler(context: IQueryContext) {
        this.loggerService.debug(
            `StandardDashboardWorker.changePendingQueryContextHandler`,
            // reason,
            context
        );
        // cancel and recreate token
        this.filterCancellationToken.cancel();
        // create new instance in another place in memory, currently running functions will still use previous reference
        this.filterCancellationToken = new PromiseCancellationToken();

        await this.changePendingQueryContextImpl(
            context,
            this.filterCancellationToken
        ).catch((e) => {
            if (e instanceof OperationCancelledError) {
                this.loggerService.error(e.message);
                return;
            }
            throw e;
        });
    }

    /**
     * Implementation method for changing the pending query context.
     * Responsible for running filter data loaders based on the new context.
     *
     * @param context - The new pending query context.
     * @param filterCancellationToken - Token for cancelling filter loading operations.
     */
    async changePendingQueryContextImpl(
        context: IQueryContext,
        filterCancellationToken: PromiseCancellationToken
    ) {
        filterCancellationToken.throwIfCancellationRequested();

        // execute each loader
        const filterLoadersPromises: Promise<void>[] = Object.values(
            this.filterDataLoaders
        ).map(async (loader) => {
            await loader.run(structuredClone(context), filterCancellationToken);
        });

        await Promise.all(filterLoadersPromises);
    }

    /**
     * Applies the changed filters by updating the query context.
     * This method should be called to apply changes made to filters.
     */
    applyChangedFilters() {
        // TODO Migrate do ApplyButtonComponent
        this.isApplyButtonDisabled = true;
        this.queryContextService.applyPendingQueryContext();
    }

    /**
     * Resets the query cache and notifies the user.
     * This method should be called to clear cached query results.
     */
    resetCache() {
        this.queryCache.resetCache();
        this.notification.blank('Reset cache', 'Cache reset successfully.');
    }

    /**
     * Reloads data by resetting the cache and applying the pending query context.
     * This method should be called to refresh the data displayed in the dashboard.
     */
    reloadData() {
        this.queryCache.resetCache();
        this.queryContextService.applyPendingQueryContext();
    }

    /**
     * Saves the current query context for later retrieval and sharing.
     * The saved context can be accessed via a unique context ID.
     * @param name - The friendly name of the saved context.
     * @param isPublic - Indicates whether the saved context is public or private.
     */
    async saveCurrentContext(options?: { name?: string; isPublic?: boolean }) {
        this.dashboardService.saveDashboardView(options);
    }

    /**
     * Retrieves a saved query context based on the 'contextId' query parameter.
     *
     * @returns {Promise<IQueryContext | void>} - A Promise that resolves to the saved query context if it exists and is found, otherwise void.
     *
     * @remarks
     * This method first checks if a 'contextId' query parameter is present in the current route.
     * If a 'contextId' is found, it attempts to retrieve a saved context with that ID.
     * If a saved context is found, it is returned.
     * If a saved context is not found, an error notification is displayed and the method returns void.
     *
     * @example
     * // If a saved context with the 'contextId' from the current route exists, it is returned
     * const savedContext = await getSavedContext();
     *
     * // If a saved context with the 'contextId' from the current route does not exist, an error notification is displayed and the method returns void
     * const savedContext = await getSavedContext();
     */
    private async getSavedDashboardViewFromURL(): Promise<
        DashboardView | undefined
    > {
        const contextId =
            this.activatedRoute.snapshot.queryParamMap.get('contextId');

        // if not null
        if (contextId) {
            const savedDashboardView = await this.data.getSavedDashboardView(
                contextId
            );
            if (!savedDashboardView) {
                this.notification.error(
                    'Dashboard view does not exist',
                    `Dashboard view with id '${contextId}' does not exist.`
                );
                return undefined;
            }
            return savedDashboardView;
        }
        return undefined;
    }

    /**
     * Orchestrates BI jobs from segments.

     */
    async requestNewSegmentsProcessing() {
        await this.dashboardService.requestNewSegmentsProcessing(this.modal);
    }

    /**
     * Shows the documentation for the dashboard.
     */
    public showDashboardDocumentation() {
        this.dashboardService.showDashboardDocumentation(
            this.dashboardDocumentation
        );
    }
}
